import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, useTheme } from '@mui/material/styles';

import {
    Box,
    Stack,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Avatar,
} from '@mui/material';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { isNumericOnly } from '../../utils/password-validation';

import {
    Type,
    isDrawerOpen,
    drawerMaxWidth,
    SupervisorDefinition,
    SupervisorGeneralDefinition,
    AdminDefinition,
    AdminGeneralDefinition,
    AdminGeneralApplyDefinition,
    UserDefinition
} from './def';

import { AppIcon } from '../appIcon';
import { DidimLogo } from '../../assets/images/didimLogo';
import { IcMenu, IcAlarm, IcAlarmDot, Refresh, QR, PhotoEmpty, Back } from '../../assets/images';
import { isWebView, toDisplayString } from '../../utils/base';
import { organizationAPI, membershipAPI } from '../../actions/imsServer';

/** Ensure menu/submenu label is always a string (t() can return object with value/label in some i18n setups). */
function menuLabel(t, name) {
    if (name == null) return '';
    const v = t(name);
    return toDisplayString(v, String(name));
}

const DrawerMenus = styled(Drawer, {
    shouldForwardProp: prop => prop !== 'open',
})(({ theme }) => ({
    width: drawerMaxWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': {
        backgroundColor: theme.palette.figma.mainBg,
        width: drawerMaxWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: 'hidden',
        borderRight: 'none',
        boxShadow: 'none',
        '&::before': {
            display: 'none',
        },
    },
    variants: [
        {
            props: ({ open }) => open,
            style: {
                ...openedDrawerMenus(theme),
                '& .MuiDrawer-paper': openedDrawerMenus(theme),
            },
        },
        {
            props: ({ open }) => !open,
            style: {
                ...closedDrawerMenus(theme),
                '& .MuiDrawer-paper': closedDrawerMenus(theme),
            },
        },
    ],
}));

const DrawerMenusHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    paddingLeft: theme.spacing(2),
    height: 64,
    minHeight: 64,
    borderBottom: 'none',
    backgroundColor: theme.palette.figma.block,
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const DrawerMenusContent = styled('div')(({ theme, open }) => ({
    flexGrow: 1,
    overflow: open ? 'auto' : 'hidden',
    minHeight: 500,
    backgroundColor: theme.palette.figma.block,
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
}));

const DrawerMenusFooter = styled('div')(({ theme }) => ({
    marginTop: 'auto',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    paddingLeft: theme.spacing(2),
    height: 64,
    backgroundColor: theme.palette.figma.block,
}));

const openedDrawerMenus = theme => ({
    width: drawerMaxWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedDrawerMenus = theme => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

export const Menus = ({ appType, setMenusWidth, setPageWidth, open, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const isMobile = useMediaQuery('(max-width: 768px)');
    const isTablet = useMediaQuery(
        '(min-width: 768px) and (max-width: 1024px)'
    );

    const [breadcrumb, setBreadcrumb] = useState(['']);

    const [menuOpen, setMenuOpen] = useState(appType === 'user' ? false : isDrawerOpen);

    // open prop이 전달되면 외부에서 제어 (Header에서 isMenuOpen 상태로 제어)
    // 실제로 사용할 메뉴 열림 상태: 
    // - 모바일/태블릿: open prop이 있으면 그것을 사용, 없으면 내부 state 사용
    // - 데스크탑: 항상 확장되어 있음 (true)
    const actualMenuOpen = (isMobile || isTablet) && open !== undefined ? open : (isMobile || isTablet ? menuOpen : true);

    // open prop이 전달되면 외부에서 제어 (Header에서 isMenuOpen 상태로 제어)
    useEffect(() => {
        if (open !== undefined) {
            setMenuOpen(open);
        }
    }, [open]);

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const [menus, setMenus] = useState([]);
    const [currentMenu, setCurrentMenu] = useState(null);

    const [organizationName, setOrganizationName] = useState('');
    const [meInfo, setMeInfo] = useState(null);
    const [hasNewNotifications, setHasNewNotifications] = useState(false);
    const [isUnregistering, setIsUnregistering] = useState(false);

    useEffect(() => {
        function resize() {
            setWindowWidth(window.innerWidth);
        }
        window.addEventListener('resize', resize);
        return () => {
            window.removeEventListener('resize', resize);
        };
    }, []);

    // meInfo 상태 업데이트를 위한 useEffect
    useEffect(() => {
        const storedMeInfo = JSON.parse(localStorage.getItem('meInfo'));
        setMeInfo(storedMeInfo);
    }, []);

    // 앱에서 closeMenu 이벤트 받기
    useEffect(() => {
        const handleCloseMenu = () => {
            if (open !== undefined) {
                if (onClose) {
                    onClose();
                } else {
                    window.dispatchEvent(new CustomEvent('toggleMenu', {
                        detail: { open: false },
                        bubbles: true
                    }));
                }
                if (typeof window !== 'undefined' && window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: 'menuState',
                            isOpen: false
                        })
                    );
                }
            } else {
                setMenuOpen(false);
                if (typeof window !== 'undefined' && window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: 'menuState',
                            isOpen: false
                        })
                    );
                }
            }
        };

        window.addEventListener('closeMenu', handleCloseMenu);
        return () => {
            window.removeEventListener('closeMenu', handleCloseMenu);
        };
    }, [open, onClose]);

    // isUnregistering 상태 변경 감지
    useEffect(() => {
        const handleUnregisterStateChange = (e) => {
            setIsUnregistering(e.detail?.isUnregistering || false);
        };

        window.addEventListener('unregisterStateChange', handleUnregisterStateChange);
        return () => {
            window.removeEventListener('unregisterStateChange', handleUnregisterStateChange);
        };
    }, []);

    // 메뉴 토글 이벤트 감지
    useEffect(() => {
        const handleToggleMenu = (e) => {
            if (e.detail?.open !== undefined) {
                setMenuOpen(e.detail.open);
                // 메뉴 상태를 앱에 알림 (WebView)
                if (typeof window !== 'undefined' && window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: 'menuState',
                            isOpen: e.detail.open
                        })
                    );
                }
            } else {
                setMenuOpen(prev => !prev);
                // 메뉴 상태를 앱에 알림 (WebView)
                if (typeof window !== 'undefined' && window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: 'menuState',
                            isOpen: !menuOpen
                        })
                    );
                }
            }
        };

        window.addEventListener('toggleMenu', handleToggleMenu);

        return () => {
            window.removeEventListener('toggleMenu', handleToggleMenu);
        };
    }, [menuOpen]);

    // open prop이 없을 때만 내부 window.openMenu 함수 등록
    useEffect(() => {
        if (open === undefined) {
            if (!window.openMenu) {
                window.openMenu = () => {
                    setMenuOpen(true);
                };
            }
            return () => {
                if (window.openMenu && window.openMenu.toString().includes('setMenuOpen(true)')) {
                    delete window.openMenu;
                }
            };
        }
    }, [open]);

    // localStorage 변경 감지를 위한 이벤트 리스너
    useEffect(() => {
        const handleStorageChange = e => {
            if (e.key === 'meInfo') {
                const newMeInfo = e.newValue ? JSON.parse(e.newValue) : null;
                setMeInfo(newMeInfo);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // 커스텀 이벤트 리스너 (같은 탭에서의 localStorage 변경 감지)
        const handleMeInfoChange = () => {
            const storedMeInfo = JSON.parse(localStorage.getItem('meInfo'));
            setMeInfo(storedMeInfo);
        };

        window.addEventListener('meInfoChanged', handleMeInfoChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('meInfoChanged', handleMeInfoChange);
        };
    }, []);

    // 어드민의 소속 기관 가져오기
    useEffect(() => {
        const fetchAdminOrganization = async () => {
            if (meInfo?.grade >= 50 && meInfo?.grade <= 60 && meInfo?.id) {
                try {
                    // grade 50, 60 모두 membership에서 가져오기
                    const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                    const memberships = membershipsResponse?.data?.memberships || [];
                    if (memberships.length > 0 && memberships[0]?.organization) {
                        setOrganizationName(memberships[0].organization.name || '');
                    }
                } catch (err) {
                    console.error(t('admin_organization_fetch_failed'), err);
                }
            }
        };

        fetchAdminOrganization();
    }, [meInfo, t]);

    useEffect(() => {
        // meInfo가 없어도 appType에 따라 기본 메뉴 설정
        let menuDefinition = [];

        if (appType === 'admin' && meInfo?.grade === 100) {
            menuDefinition = SupervisorDefinition;
        } else if (appType === 'admin' && meInfo?.grade >= 80) {
            menuDefinition = SupervisorGeneralDefinition;
        } else if (appType === 'admin' && meInfo?.grade >= 60) {
            const fetchAdminOrganization = async () => {
                try {
                    const response = await organizationAPI.getOrganizations({ userId: meInfo.id, state: 0 });
                    let selectedMenuDefinition = [];
                    if (response?.data?.organizations.length > 0 && Array.isArray(response.data.organizations)) {
                        selectedMenuDefinition = AdminDefinition;
                    } else {
                        selectedMenuDefinition = AdminGeneralApplyDefinition;
                    }

                    const translatedMenus = selectedMenuDefinition
                        .map(menu => ({
                            ...menu,
                            displayName: menuLabel(t, menu.name),
                        }))
                    setMenus(translatedMenus);
                } catch (apiErr) {
                    const translatedMenus = AdminGeneralApplyDefinition
                        .map(menu => ({
                            ...menu,
                            displayName: menuLabel(t, menu.name),
                        }))
                    setMenus(translatedMenus);
                }
            }
            fetchAdminOrganization();
            return;

        } else if (appType === 'admin' && meInfo?.grade >= 50) {
            menuDefinition = AdminGeneralDefinition;
        } else {
            menuDefinition = UserDefinition;
        }

        const translatedMenus = menuDefinition
            .map(menu => ({
                ...menu,
                displayName: menuLabel(t, menu.name),
            }))
        setMenus(translatedMenus);
    }, [t, appType, meInfo]);

    useEffect(() => {
        // menus가 로드되지 않았으면 breadcrumb를 설정하지 않음
        if (menus.length === 0) {
            return;
        }

        if (location.pathname === '/') {
            // grade에 따라 올바른 메뉴 찾기
            const currentMeInfo = meInfo || JSON.parse(localStorage.getItem('meInfo') || '{}');
            const grade = currentMeInfo?.grade || 0;

            let homeMenu = null;

            if (grade >= 50) {
                // superadmin 또는 admin: 기관관리
                homeMenu = menus.find(menu => menu.path === '/organizations' || menu.name === 'organization_management');
            } else {
                // user: 첫 번째 메뉴 (보통 home)
                homeMenu = menus[0];
            }

            if (homeMenu) {
                setBreadcrumb([toDisplayString(homeMenu.displayName, homeMenu.name)]);
                setCurrentMenu(homeMenu);
            } else {
                // 메뉴를 찾지 못한 경우 첫 번째 메뉴 사용
                setBreadcrumb([toDisplayString(menus[0]?.displayName, menus[0]?.name)]);
                setCurrentMenu(menus[0]);
            }
        } else {
            // 먼저 정확히 일치하는 메뉴를 찾기 (상위메뉴 우선)
            const exactMenu = menus.find(
                menu => menu.path === location.pathname
            );

            if (exactMenu) {
                // 정확히 일치하는 메뉴가 있으면 그것을 사용
                setBreadcrumb([toDisplayString(exactMenu.displayName, exactMenu.name)]);
                setCurrentMenu(exactMenu);
            } else {
                // 정확히 일치하는 메뉴가 없으면 하위메뉴 찾기
                let foundSubMenu = null;
                let foundParentMenu = null;

                for (const menu of menus) {
                    if (menu.subMenus) {
                        const subMenu = menu.subMenus.find(
                            sub => sub.path === location.pathname
                        );
                        if (subMenu) {
                            foundSubMenu = subMenu;
                            foundParentMenu = menu;
                            break;
                        }
                    }
                }

                if (foundSubMenu && foundParentMenu) {
                    // 하위메뉴를 찾은 경우
                    setBreadcrumb([toDisplayString(foundParentMenu.displayName, foundParentMenu.name), menuLabel(t, foundSubMenu.name)]);
                    setCurrentMenu(foundParentMenu);
                } else {
                    // 하위메뉴를 찾지 못한 경우 상위메뉴 찾기
                    // 가장 긴 경로로 시작하는 메뉴를 찾기 (더 정확한 매칭을 위해)
                    const matchingMenus = menus.filter(menu => {
                        return (
                            location.pathname.startsWith(menu.path) &&
                            menu.path !== '/'
                        );
                    });

                    // 가장 긴 경로를 가진 메뉴 선택
                    const parentMenu = matchingMenus.reduce((longest, current) => {
                        return current.path.length > longest.path.length ? current : longest;
                    }, matchingMenus[0]);

                    if (parentMenu) {
                        // 상위메뉴를 찾은 경우
                        let breadcrumbItems = [toDisplayString(parentMenu.displayName, parentMenu.name)];

                        // 정확한 하위메뉴를 찾을 수 없는 경우 경로에서 추출
                        const pathParts = location.pathname
                            .replace(parentMenu.path, '')
                            .split('/')
                            .filter(Boolean);
                        if (pathParts.length > 0) {
                            const lastPart =
                                pathParts[pathParts.length - 1];
                            // 숫자나 ID인 경우 건너뛰기
                            if (!isNumericOnly(lastPart)) {
                                breadcrumbItems.push(
                                    lastPart.charAt(0).toUpperCase() +
                                    lastPart.slice(1)
                                );
                            }
                        }

                        setBreadcrumb(breadcrumbItems);
                        setCurrentMenu(parentMenu);
                    } else {
                        // 메뉴에서 찾을 수 없는 경우 기존 방식 사용
                        setBreadcrumb(
                            location.pathname
                                .split('/')
                                .filter(Boolean)
                                .map(
                                    str =>
                                        str.charAt(0).toUpperCase() + str.slice(1)
                                )
                        );
                        setCurrentMenu(null);
                    }
                }
            }
        }

    }, [location.pathname, menus, t, meInfo]);

    useEffect(() => {
        if (isMobile || isTablet) {
            setMenusWidth(0);
            setPageWidth(windowWidth - drawerMaxWidth);
        } else {
            // 데스크탑일 때는 항상 확장된 너비 사용
            setMenusWidth(drawerMaxWidth);
            setPageWidth(windowWidth - drawerMaxWidth);
        }

        // 메뉴 상태를 앱에 알림 (WebView)
        if (typeof window !== 'undefined' && window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'menuState',
                    isOpen: menuOpen
                })
            );
        }
    }, [
        appType,
        menuOpen,
        isMobile,
        isTablet,
        windowWidth,
        setMenusWidth,
        setPageWidth,
    ]);

    const handleQRCodeClick = () => {
        navigate('/users/qr');
    };

    // 오른쪽 아이콘/버튼 유무 (제목 maxWidth 분기용)
    const hasRightSectionContent = useMemo(() => {
        let currentSub = null;
        if (currentMenu?.subMenus) {
            currentSub = currentMenu.subMenus.find(sub => sub.path === location.pathname);
        }
        const customIcon = currentSub?.customRightIcon || currentMenu?.customRightIcon;
        if (customIcon) return true;
        const rs = currentSub?.rightSection || currentMenu?.rightSection;
        if (!rs) return false;
        if (rs === 'unregister') return true;
        if (Array.isArray(rs)) return rs.some(v => ['unregister', 'qrCode', 'refresh', 'alarm'].includes(v));
        return true;
    }, [currentMenu, location.pathname]);

    // 알림 목록으로 이동할 때 마지막 확인 시간 업데이트
    const handleNotificationListClick = () => {
        localStorage.setItem('lastNotificationCheck', new Date().toISOString());
        setHasNewNotifications(false);
        navigate('/notices/notification-list');
    };

    const runNavigation = path => {
        // 이미 해당 경로에 있으면 replace로 이동해 히스토리 중복 방지 (메뉴→home→뒤로가기 시 메뉴가 다시 뜨는 현상 방지)
        const currentPath = (location.pathname || '').replace(/\/$/, '') || '/';
        const targetPath = (path || '').replace(/\/$/, '') || '/';
        navigate(path, { replace: currentPath === targetPath });

        // 메뉴 닫기: open prop이 있으면 onClose 호출 또는 toggleMenu 이벤트 발생, 없으면 내부 state 업데이트
        if (open !== undefined) {
            if (onClose) {
                onClose();
            } else {
                window.dispatchEvent(new CustomEvent('toggleMenu', {
                    detail: { open: false },
                    bubbles: true
                }));
            }
            if (typeof window !== 'undefined' && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({ type: 'menuState', isOpen: false })
                );
            }
        } else {
            setMenuOpen(false);
            if (typeof window !== 'undefined' && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({ type: 'menuState', isOpen: false })
                );
            }
        }
    };

    /**
     * 모바일 헤더 좌측: 상위/서브메뉴 leftSection + 서브메뉴의 leftSectionWhenStateMatches( def.js )
     */
    const mobileLeftSection = useMemo(() => {
        let leftSectionToUse = currentMenu?.leftSection;
        let foundSubMenu = null;
        if (currentMenu?.subMenus) {
            foundSubMenu = currentMenu.subMenus.find(
                sub => sub.path === location.pathname
            );
            if (foundSubMenu) {
                leftSectionToUse =
                    foundSubMenu.leftSection || currentMenu?.leftSection;
            }
        }
        const when = foundSubMenu?.leftSectionWhenStateMatches;
        if (
            when &&
            location.state &&
            Object.keys(when).length > 0 &&
            Object.entries(when).every(
                ([key, val]) => location.state[key] === val
            )
        ) {
            leftSectionToUse =
                foundSubMenu.leftSectionWhenStateResult || 'back';
        }
        return leftSectionToUse;
    }, [currentMenu, location.pathname, location.state]);

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                gap={2}
                sx={{ marginLeft: '10px' }}
            >
                {isMobile || isTablet ? (
                    <>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
                            {(menus.length > 1 || isMobile) && (
                                <IconButton
                                    sx={{
                                        width: '20px',
                                        height: '20px',
                                        p: 0,
                                        zIndex: 1000,
                                        position: 'relative',
                                        pointerEvents: 'auto',
                                        cursor: 'pointer',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if (mobileLeftSection === 'back') {
                                            navigate(-1);
                                        } else {
                                            if (open !== undefined) {
                                                window.dispatchEvent(new CustomEvent('toggleMenu', {
                                                    detail: { open: true },
                                                    bubbles: true
                                                }));
                                            } else {
                                                setMenuOpen(true);
                                            }
                                        }
                                    }}
                                >
                                    {mobileLeftSection === 'back' ? (
                                        <Back size={20} />
                                    ) : (
                                        <IcMenu size={20} />
                                    )}
                                </IconButton>
                            )}

                            <Stack direction="row" alignItems="center" justifyContent="center" gap={1} sx={{ flex: 1 }}>
                                {(!isMobile ||
                                    (JSON.parse(localStorage.getItem('meInfo'))
                                        ?.grade >= 50
                                        && JSON.parse(localStorage.getItem('meInfo'))?.grade <= 60)) && (
                                        <Typography
                                            variant="subtitle1"
                                            component="div"
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 500,
                                            }}
                                            onClick={() => runNavigation('/')}
                                        >
                                            {organizationName || t('didim')}
                                        </Typography>
                                    )
                                }
                            </Stack>
                        </Stack>

                        <Drawer
                            open={actualMenuOpen}
                            variant="temporary"
                            elevation={0}
                            sx={{
                                width: '100vw',
                                '& .MuiDrawer-paper': {
                                    backgroundColor: theme.palette.figma.mainBg,
                                    width: '100vw',
                                    height: '100vh',
                                    maxWidth: '100vw',
                                    boxShadow: 'none',
                                    '&::before': {
                                        display: 'none',
                                    },
                                },
                            }}
                            onClose={() => {
                                setMenuOpen(false);
                            }}
                        >
                            <Box
                                role="presentation"
                                sx={{
                                    marginTop: 2,
                                    width: '100%',
                                    height: '100vh',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: theme.palette.figma.mainBg,
                                }}
                                onClick={() => {
                                    setMenuOpen(false);
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                    sx={{ px: 2, mb: 2 }}
                                >
                                    {!isMobile && (
                                        <Typography
                                            variant="subtitle1"
                                            component="div"
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 500,
                                            }}
                                            onClick={() => runNavigation('/')}
                                        >
                                            {organizationName || t('didim')}
                                        </Typography>
                                    )}
                                </Stack>

                                <List
                                    sx={{
                                        flex: 1,
                                        overflow: 'auto',
                                        paddingBottom: '80px',
                                    }}
                                >
                                    {menus.filter(menu => !menu.hidden).map((item, index) => (
                                        <React.Fragment key={index}>
                                            <ListItem
                                                disablePadding
                                                sx={{
                                                    display: 'block',
                                                    backgroundColor: 'transparent',
                                                    mb: 1,
                                                    '& .MuiListItemButton-root': {
                                                        py: 1.5,
                                                    },
                                                }}
                                            >
                                                <ListItemButton
                                                    onClick={() => {
                                                        if (item.subMenus && item.subMenus.length > 0) {
                                                            // 하위메뉴가 있으면 첫 번째 하위메뉴로 이동
                                                            runNavigation(item.subMenus[0].path);
                                                        } else {
                                                            // 하위메뉴가 없으면 상위메뉴로 이동
                                                            runNavigation(item.path);
                                                        }
                                                    }}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                        {item.icon && (
                                                            <ListItemIcon
                                                                sx={{
                                                                    color: theme.palette
                                                                        .figma.white[60],
                                                                    minWidth: '40px',
                                                                }}
                                                            >
                                                                {React.createElement(item.icon, {
                                                                    color: theme.palette.figma.white[60],
                                                                    size: 20,
                                                                    theme: theme,
                                                                })}
                                                            </ListItemIcon>
                                                        )}
                                                        <ListItemText
                                                            primary={toDisplayString(item.displayName, item.name)}
                                                            sx={{
                                                                '& .MuiTypography-root':
                                                                {
                                                                    color: theme
                                                                        .palette
                                                                        .text.main,
                                                                    fontSize:
                                                                        '18px',
                                                                    fontWeight: 500,
                                                                    letterSpacing:
                                                                        '-0.54px',
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                    {/* customRightIcon 지원 */}
                                                    {item.customRightIcon && (
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation(); // ListItemButton의 onClick 방지
                                                                if (item.onRightIconClick) {
                                                                    item.onRightIconClick();
                                                                } else if (item.onRightIconClickPath) {
                                                                    runNavigation(item.onRightIconClickPath);
                                                                }
                                                            }}
                                                            sx={{
                                                                padding: '4px',
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.figma.white[20],
                                                                },
                                                            }}
                                                        >
                                                            {React.isValidElement(item.customRightIcon)
                                                                ? item.customRightIcon
                                                                : React.createElement(item.customRightIcon, { size: 20 })}
                                                        </IconButton>
                                                    )}
                                                </ListItemButton>
                                            </ListItem>

                                            {/* 하위 메뉴 */}
                                            {item.subMenus &&
                                                item.subMenus.length > 0 && (
                                                    <Box sx={{ pl: 0, pb: 1 }}>
                                                        {item.subMenus.filter(subItem => !subItem.hidden).map(
                                                            (subItem, subIndex) => (
                                                                <ListItem
                                                                    key={subIndex}
                                                                    disablePadding
                                                                    sx={{
                                                                        display:
                                                                            'block',
                                                                        backgroundColor:
                                                                            'transparent',
                                                                        mb: 1.25,
                                                                    }}
                                                                >
                                                                    <ListItemButton
                                                                        onClick={() =>
                                                                            runNavigation(
                                                                                subItem.path
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            py: 1,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                        }}
                                                                    >
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                                            <ListItemIcon
                                                                                sx={{
                                                                                    color: theme
                                                                                        .palette
                                                                                        .figma
                                                                                        .white[60],
                                                                                    minWidth:
                                                                                        '40px',
                                                                                }}
                                                                            >
                                                                                {/* 빈 아이콘 공간 */}
                                                                            </ListItemIcon>
                                                                            <ListItemText
                                                                                primary={menuLabel(t, subItem.name)}
                                                                                sx={{
                                                                                    '& .MuiTypography-root':
                                                                                    {
                                                                                        color: theme
                                                                                            .palette
                                                                                            .figma
                                                                                            .white[60],
                                                                                        fontSize:
                                                                                            '14px',
                                                                                        fontWeight: 500,
                                                                                        letterSpacing:
                                                                                            '-0.42px',
                                                                                    },
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </ListItemButton>
                                                                </ListItem>
                                                            )
                                                        )}
                                                    </Box>
                                                )}
                                        </React.Fragment>
                                    ))}
                                </List>

                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={2.5}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        width: '100%',
                                        px: '20px',
                                        py: '50px',
                                        bgcolor: theme.palette.figma.mainBg,
                                    }}
                                    onClick={() => {
                                        runNavigation('/users/mypage');
                                    }}
                                >
                                    <Avatar
                                        alt="User Avatar"
                                        src={JSON.parse(
                                            localStorage.getItem('meInfo')
                                        )?.profilePath || undefined}
                                        onClick={() => { }}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            cursor: 'pointer',
                                            borderRadius: '40px',
                                            '& img': {
                                                filter: !JSON.parse(
                                                    localStorage.getItem('meInfo')
                                                )?.profilePath
                                                    ? theme.palette.mode === 'dark'
                                                        ? 'brightness(0) invert(1)'
                                                        : 'none'
                                                    : 'none',
                                            },
                                        }}
                                    >
                                        {!JSON.parse(
                                            localStorage.getItem('meInfo')
                                        )?.profilePath && (
                                                <PhotoEmpty size={40} />
                                            )}
                                    </Avatar>
                                    <Typography
                                        variant="h7"
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 500,
                                            letterSpacing: '-0.42px',
                                        }}
                                    >
                                        {
                                            JSON.parse(
                                                localStorage.getItem('meInfo')
                                            )?.name
                                        }
                                    </Typography>
                                </Stack>
                            </Box>
                        </Drawer>
                    </>
                ) : (
                    <>
                        {/* appbar menus */}
                        {Type === 'AppBar' && (
                            <Stack direction="row" alignItems="center" gap={5}>
                                <IconButton
                                    size="large"
                                    edge="start"
                                    color="inherit"
                                    aria-label="menu"
                                >
                                    <AppIcon />
                                </IconButton>
                                {menus.map((item, index) => (
                                    <Typography
                                        variant="h7"
                                        component="div"
                                        sx={{
                                            flexGrow: 1,
                                            '&:hover': { color: theme.palette.figma.white[60] },
                                        }}
                                        onClick={() => {
                                            runNavigation(item.path);
                                        }}
                                    >
                                        {toDisplayString(item.displayName, item.name)}
                                    </Typography>
                                ))}
                            </Stack>
                        )}

                        {/* drawer menus */}
                        {Type === 'Drawer' && (
                            <>
                                {/* 데스크톱에서 메뉴명은 헤더의 중앙 영역에 표시됨 */}
                                <Box sx={{ display: 'none' }}>
                                    {/* 이 영역은 데스크톱에서 숨김 처리 */}
                                </Box>

                                <DrawerMenus
                                    variant="permanent"
                                    open={actualMenuOpen}
                                    elevation={0}
                                >
                                    <DrawerMenusHeader>
                                        {/* 데스크탑일 때는 항상 확장된 상태, 모바일/태블릿일 때는 menuOpen state에 따라 */}
                                        {((!isMobile && !isTablet) || menuOpen) ? (
                                            <Stack
                                                width={drawerMaxWidth}
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <Stack
                                                    alignItems="center"
                                                    width="100%"
                                                    justifyContent="center"
                                                >
                                                    <Box
                                                        sx={{
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            height: '32px',
                                                        }}
                                                        onClick={() =>
                                                            runNavigation('/')
                                                        }
                                                    >
                                                        <DidimLogo
                                                            size={120}
                                                            color={
                                                                theme.palette.figma
                                                                    .white[100]
                                                            }
                                                        />
                                                    </Box>
                                                    <Typography
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '14px',
                                                            fontWeight: 500,
                                                            letterSpacing: '-0.42px',
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {meInfo?.grade >= 50 && meInfo?.grade <= 60 ? organizationName : ''}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <Stack
                                                sx={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                <IconButton
                                                    onClick={() => {
                                                        setMenuOpen(!menuOpen);
                                                    }}
                                                    sx={{
                                                        color: theme.palette.figma
                                                            .icon,
                                                    }}
                                                >
                                                    <ChevronRightIcon />
                                                </IconButton>
                                            </Stack>
                                        )}
                                    </DrawerMenusHeader>

                                    <DrawerMenusContent open={actualMenuOpen}>
                                        <List sx={{ py: 2 }}>
                                            {menus.filter(menu => !menu.hidden).map((item, index) => (
                                                <React.Fragment key={index}>
                                                    <ListItem
                                                        disablePadding
                                                        sx={{
                                                            display: 'block',
                                                            backgroundColor: 'transparent',
                                                            borderRadius: '8px',
                                                            '& .MuiListItemButton-root': {
                                                                borderRadius: '8px',
                                                                minHeight: 52,
                                                                justifyContent: ((!isMobile && !isTablet) || menuOpen) ? 'initial' : 'center',
                                                                transition: 'all 0.2s ease-in-out',
                                                                '&.Mui-selected': {
                                                                    backgroundColor: theme.palette.figma.white[5],
                                                                    '&:hover': {
                                                                        backgroundColor: theme.palette.figma.white[20],
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <ListItemButton
                                                            selected={location.pathname === item.path ||
                                                                (item.subMenus && item.subMenus.some(sub => location.pathname === sub.path)) ||
                                                                (item.path !== '/' && location.pathname.startsWith(item.path))}
                                                            onClick={() => {
                                                                if (item.subMenus && item.subMenus.length > 0) {
                                                                    // 하위메뉴가 있으면 첫 번째 하위메뉴로 이동
                                                                    runNavigation(item.subMenus[0].path);
                                                                } else {
                                                                    // 하위메뉴가 없으면 상위메뉴로 이동
                                                                    runNavigation(item.path);
                                                                }
                                                            }}
                                                        >
                                                            {item.icon && (() => {
                                                                const isSelected = location.pathname === item.path ||
                                                                    (item.subMenus && item.subMenus.some(sub => location.pathname === sub.path)) ||
                                                                    (item.path !== '/' && location.pathname.startsWith(item.path));
                                                                const iconColor = isSelected ? theme.palette.figma.white[100] : theme.palette.figma.icon;
                                                                return (
                                                                    <ListItemIcon
                                                                        sx={{
                                                                            minWidth: 34,
                                                                            justifyContent: 'flex-start',
                                                                            color: iconColor,
                                                                            transition: 'color 0.2s ease-in-out',
                                                                        }}
                                                                    >
                                                                        {React.createElement(item.icon, {
                                                                            color: iconColor,
                                                                            size: 22,
                                                                            theme: theme,
                                                                        })}
                                                                    </ListItemIcon>
                                                                );
                                                            })()}
                                                            <ListItemText
                                                                primary={toDisplayString(item.displayName, item.name)}
                                                                sx={{
                                                                    minWidth: 0,
                                                                    opacity: ((!isMobile && !isTablet) || menuOpen) ? 1 : 0,
                                                                    transition: 'opacity 0.2s ease-in-out',
                                                                    '& .MuiTypography-root': {
                                                                        fontSize: '16px',
                                                                        fontWeight: 500,
                                                                        letterSpacing: '-0.48px',
                                                                        transition: 'color 0.2s ease-in-out',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        color: (location.pathname === item.path ||
                                                                            (item.subMenus && item.subMenus.some(sub => location.pathname === sub.path)) ||
                                                                            (item.path !== '/' && location.pathname.startsWith(item.path)))
                                                                            ? theme.palette.figma.white[100]
                                                                            : theme.palette.figma.icon,
                                                                    },
                                                                }}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>

                                                    {/* 하위 메뉴 - 데스크탑에서는 숨김 */}
                                                    {item.subMenus &&
                                                        item.subMenus.length > 0 &&
                                                        menuOpen &&
                                                        (isMobile || isTablet) && (
                                                            <Box
                                                                sx={{
                                                                    pl: 2,
                                                                    pb: 1,
                                                                    ml: 1,
                                                                    borderLeft: `2px solid ${theme.palette.figma.white[20]}`,
                                                                }}
                                                            >
                                                                {item.subMenus.filter(subItem => !subItem.hidden).map(
                                                                    (
                                                                        subItem,
                                                                        subIndex
                                                                    ) => (
                                                                        <ListItem
                                                                            key={subIndex}
                                                                            disablePadding
                                                                            sx={{
                                                                                display: 'block',
                                                                                backgroundColor: 'transparent',
                                                                                mb: 0.5,
                                                                                mx: 0.5,
                                                                                '& .MuiListItemButton-root': {
                                                                                    py: 1,
                                                                                    borderRadius: '8px',
                                                                                    minHeight: 40,
                                                                                    px: 1.5,
                                                                                    transition: 'all 0.2s ease-in-out',
                                                                                    '&:hover': {
                                                                                        backgroundColor: theme.palette.figma.white[10],
                                                                                        transform: 'translateX(2px)',
                                                                                    },
                                                                                    '&.Mui-selected': {
                                                                                        backgroundColor: theme.palette.figma.white[20],
                                                                                        '&:hover': {
                                                                                            backgroundColor: theme.palette.figma.white[20],
                                                                                        },
                                                                                    },
                                                                                },
                                                                            }}
                                                                        >
                                                                            <ListItemButton
                                                                                selected={location.pathname === subItem.path}
                                                                                onClick={() =>
                                                                                    runNavigation(subItem.path)
                                                                                }
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'space-between',
                                                                                }}
                                                                            >
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                                                    <ListItemIcon
                                                                                        sx={{
                                                                                            color: theme.palette.figma.white[50],
                                                                                            minWidth: '32px',
                                                                                            justifyContent: 'center',
                                                                                            transition: 'color 0.2s ease-in-out',
                                                                                        }}
                                                                                    >
                                                                                        <Box
                                                                                            sx={{
                                                                                                width: '6px',
                                                                                                height: '6px',
                                                                                                borderRadius: '50%',
                                                                                                backgroundColor: theme.palette.figma.white[50],
                                                                                                transition: 'all 0.2s ease-in-out',
                                                                                            }}
                                                                                        />
                                                                                    </ListItemIcon>
                                                                                    <ListItemText
                                                                                        primary={menuLabel(t, subItem.name)}
                                                                                        sx={{
                                                                                            '& .MuiTypography-root': {
                                                                                                color: theme.palette.figma.white[70],
                                                                                                fontSize: '14px',
                                                                                                fontWeight: 400,
                                                                                                letterSpacing: '-0.42px',
                                                                                                transition: 'color 0.2s ease-in-out',
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                                {/* customRightIcon 지원 (subMenus - mobile/tablet) */}
                                                                                {subItem.customRightIcon && (
                                                                                    <IconButton
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation(); // ListItemButton의 onClick 방지
                                                                                            if (subItem.onRightIconClick) {
                                                                                                subItem.onRightIconClick();
                                                                                            } else if (subItem.onRightIconClickPath) {
                                                                                                runNavigation(subItem.onRightIconClickPath);
                                                                                            }
                                                                                        }}
                                                                                        sx={{
                                                                                            padding: '4px',
                                                                                            '&:hover': {
                                                                                                backgroundColor: theme.palette.figma.white[20],
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        {React.isValidElement(subItem.customRightIcon)
                                                                                            ? subItem.customRightIcon
                                                                                            : React.createElement(subItem.customRightIcon, { size: 20 })}
                                                                                    </IconButton>
                                                                                )}
                                                                            </ListItemButton>
                                                                        </ListItem>
                                                                    )
                                                                )}
                                                            </Box>
                                                        )}
                                                </React.Fragment>
                                            ))}
                                        </List>
                                    </DrawerMenusContent>

                                    <DrawerMenusFooter>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            gap={2.5}
                                            onClick={() => {
                                                runNavigation('/users/mypage');
                                            }}
                                        >
                                            <Avatar
                                                alt="User Avatar"
                                                src={JSON.parse(
                                                    localStorage.getItem(
                                                        'meInfo'
                                                    )
                                                )?.profilePath || undefined}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    cursor: 'pointer',
                                                    borderRadius: '40px',
                                                    '& img': {
                                                        filter: !JSON.parse(
                                                            localStorage.getItem(
                                                                'meInfo'
                                                            )
                                                        )?.profilePath
                                                            ? theme.palette.mode ===
                                                                'dark'
                                                                ? 'brightness(0) invert(1)'
                                                                : 'none'
                                                            : 'none',
                                                    },
                                                }}
                                            >
                                                {!JSON.parse(
                                                    localStorage.getItem(
                                                        'meInfo'
                                                    )
                                                )?.profilePath && (
                                                        <PhotoEmpty size={40} />
                                                    )}
                                            </Avatar>
                                            {((!isMobile && !isTablet) || menuOpen) && (
                                                <Typography
                                                    variant="h7"
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 500,
                                                        letterSpacing: '-0.42px',
                                                        maxWidth: 160,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {
                                                        JSON.parse(
                                                            localStorage.getItem(
                                                                'meInfo'
                                                            )
                                                        )?.name
                                                    }
                                                </Typography>
                                            )}
                                        </Stack>
                                    </DrawerMenusFooter>
                                </DrawerMenus>
                            </>
                        )}
                    </>
                )
                }
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                    }}
                >
                    {/* DIDIM 로고 */}
                    {currentMenu?.textType === 'logo' && (
                        <DidimLogo
                            size={94}
                            color={theme.palette.figma.white[100]}
                        />
                    )}
                    {/* breadcrumb */}
                    {appType === 'admin' && <Typography
                        variant="subtitle1"
                        component="div"
                        sx={{
                            fontSize: '16px',
                            fontWeight: 500

                        }}
                    > {toDisplayString(breadcrumb.length > 1 && breadcrumb[1] !== '' ? breadcrumb[1] : breadcrumb[0])} {' > '}
                    </Typography>}
                </Box>
            </Stack>
            {/* 중앙: 메뉴명 (오른쪽 콘텐츠 유무에 따라 여유 공간 분기) */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100%',
                    maxWidth: hasRightSectionContent ? 'calc(100% - 220px)' : 'calc(100% - 100px)',
                    minWidth: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    zIndex: 1,
                }}
            >
                {currentMenu?.textType === 'title' && (
                    <Typography
                        variant="subtitle1"
                        component="div"
                        sx={{
                            fontSize: '16px',
                            fontWeight: 500,
                            pointerEvents: 'auto',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                        }}
                    >
                        {toDisplayString(breadcrumb.length > 1 && breadcrumb[1] !== '' ? breadcrumb[1] : breadcrumb[0])}
                    </Typography>
                )}
            </Stack>

            {/* 오른쪽: 알림 아이콘 */}
            <Box sx={{
                position: 'relative',
                flex: 1,
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                pointerEvents: 'auto',
                zIndex: 2,
            }}>
                {/* 현재 경로에 해당하는 subMenu 찾기 */}
                {(() => {
                    let currentSubMenu = null;
                    if (currentMenu?.subMenus) {
                        currentSubMenu = currentMenu.subMenus.find(
                            sub => sub.path === location.pathname
                        );
                    }
                    const rightSectionToUse = currentSubMenu?.rightSection || currentMenu?.rightSection;
                    const customRightIcon = currentSubMenu?.customRightIcon || currentMenu?.customRightIcon;
                    const onRightIconClick = currentSubMenu?.onRightIconClick || currentMenu?.onRightIconClick;
                    const onRightIconClickPath = currentSubMenu?.onRightIconClickPath || currentMenu?.onRightIconClickPath;

                    return (
                        <>
                            {/* customRightIcon 표시 (rightSection 아이콘들과 같은 위치) */}
                            {customRightIcon && (
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if (onRightIconClick) {
                                            onRightIconClick();
                                        } else if (onRightIconClickPath) {
                                            navigate(onRightIconClickPath);
                                            if (open !== undefined) {
                                                if (onClose) {
                                                    onClose();
                                                } else {
                                                    window.dispatchEvent(new CustomEvent('toggleMenu', {
                                                        detail: { open: false },
                                                        bubbles: true
                                                    }));
                                                }
                                            } else {
                                                setMenuOpen(false);
                                            }
                                        }
                                    }}
                                    sx={{
                                        width: '20px',
                                        height: '20px',
                                        p: 0,
                                        color: theme.palette.figma.icon,
                                        position: 'relative',
                                        zIndex: 10,
                                        pointerEvents: 'auto',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: theme.palette.figma.white[20],
                                        },
                                    }}
                                >
                                    {React.isValidElement(customRightIcon)
                                        ? customRightIcon
                                        : React.createElement(customRightIcon, { size: 20 })}
                                </IconButton>
                            )}
                            {/* rightSection이 배열인 경우 처리 */}
                            {Array.isArray(rightSectionToUse) ? (
                                <>
                                    {/* 첫 번째 아이콘 (좌측) */}
                                    {rightSectionToUse[0] === 'qrCode' && isWebView() && (
                                        <IconButton
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                p: 0,
                                                color: theme.palette.figma.icon,
                                            }}
                                            onClick={handleQRCodeClick}
                                        >
                                            <QR
                                                size={20}
                                                color={theme.palette.figma.icon}
                                            />
                                        </IconButton>
                                    )}
                                    {rightSectionToUse[0] === 'refresh' && (
                                        <IconButton
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                p: 0,
                                                color: theme.palette.figma.icon,
                                            }}
                                            onClick={() => window.location.reload()}
                                        >
                                            <Refresh />
                                        </IconButton>
                                    )}
                                    {rightSectionToUse[0] === 'alarm' && isWebView() && (
                                        <IconButton
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                p: 0,
                                                color: theme.palette.figma.icon,
                                            }}
                                            onClick={handleNotificationListClick}
                                        >
                                            {hasNewNotifications ? (
                                                <IcAlarmDot
                                                    size={20}
                                                    color={theme.palette.figma.icon}
                                                />
                                            ) : (
                                                <IcAlarm
                                                    size={20}
                                                    color={theme.palette.figma.icon}
                                                />
                                            )}
                                        </IconButton>
                                    )}

                                    {/* 두 번째 아이콘 (우측) */}
                                    {rightSectionToUse[1] === 'qrCode' && isWebView() && (
                                        <IconButton
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                p: 0,
                                                color: theme.palette.figma.icon,
                                            }}
                                            onClick={handleNotificationListClick}
                                        >
                                            <QR
                                                size={20}
                                                color={theme.palette.figma.icon}
                                            />
                                        </IconButton>
                                    )}
                                    {rightSectionToUse[1] === 'refresh' && (
                                        <IconButton
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                p: 0,
                                                color: theme.palette.figma.icon,
                                            }}
                                            onClick={() => window.location.reload()}
                                        >
                                            <Refresh />
                                        </IconButton>
                                    )}
                                    {rightSectionToUse[1] === 'alarm' && (
                                        <IconButton
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                p: 0,
                                                color: theme.palette.figma.icon,
                                            }}
                                            onClick={handleNotificationListClick}
                                        >
                                            {hasNewNotifications ? (
                                                <IcAlarmDot
                                                    size={20}
                                                    color={theme.palette.figma.icon}
                                                />
                                            ) : (
                                                <IcAlarm
                                                    size={20}
                                                    color={theme.palette.figma.icon}
                                                />
                                            )}
                                        </IconButton>
                                    )}
                                    {rightSectionToUse[0] === 'unregister' && (
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                color: theme.palette.figma.icon,
                                                letterSpacing: '-0.42px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                // 커스텀 이벤트 발생
                                                window.dispatchEvent(new CustomEvent('unregisterClick', { detail: { type: 'unregister' } }));
                                            }}
                                        >
                                            {isUnregistering ? t('cancel') : t('withdrawn')}
                                        </Typography>
                                    )}
                                    {rightSectionToUse[1] === 'unregister' && (
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                color: theme.palette.figma.icon,
                                                letterSpacing: '-0.42px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                // 커스텀 이벤트 발생
                                                window.dispatchEvent(new CustomEvent('unregisterClick', { detail: { type: 'unregister' } }));
                                            }}
                                        >
                                            {isUnregistering ? t('cancel') : t('withdrawn')}
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                /* 기존 단일 rightSection 처리 */
                                <>
                                    {rightSectionToUse === 'unregister' && (
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                color: theme.palette.figma.icon,
                                                letterSpacing: '-0.42px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                // 커스텀 이벤트 발생
                                                window.dispatchEvent(new CustomEvent('unregisterClick', { detail: { type: 'unregister' } }));
                                            }}
                                        >
                                            {isUnregistering ? t('cancel') : t('withdrawn')}
                                        </Typography>
                                    )}
                                    {rightSectionToUse === 'qrCode' && isWebView() && (
                                        <IconButton
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                p: 0,
                                                color: theme.palette.figma.icon,
                                            }}
                                            onClick={handleNotificationListClick}
                                        >
                                            <QR
                                                size={20}
                                                color={theme.palette.figma.icon}
                                            />
                                        </IconButton>
                                    )}
                                    {rightSectionToUse === 'refresh' && (
                                        <IconButton
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                p: 0,
                                                color: theme.palette.figma.icon,
                                            }}
                                            onClick={() => window.location.reload()}
                                        >
                                            <Refresh />
                                        </IconButton>
                                    )}
                                    {rightSectionToUse === 'alarm' && (
                                        <IconButton
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                p: 0,
                                                color: theme.palette.figma.icon,
                                            }}
                                            onClick={handleNotificationListClick}
                                        >
                                            {hasNewNotifications ? (
                                                <IcAlarmDot
                                                    size={20}
                                                    color={theme.palette.figma.icon}
                                                />
                                            ) : (
                                                <IcAlarm
                                                    size={20}
                                                    color={theme.palette.figma.icon}
                                                />
                                            )}
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </>
                    );
                })()}
            </Box >
        </>
    );
};
